import React, { ReactNode, useContext, useState, useRef } from 'react';
import Snackbar from '@mui/material/Snackbar';
import Alert, { AlertProps, AlertColor } from '@mui/material/Alert';

interface SnackbarMessage {
    key: string;
    message: string;
    severity: AlertColor;
    timeout: number;
}

export interface SnackbarType {
    open: (message: string, severity: AlertColor, timeoutMilis?: number) => void;
    info: (message: string, timeoutMilis?: number) => void;
    success: (message: string, timeoutMilis?: number) => void;
    error: (message: string, timeoutMilis?: number) => void;
    warning: (message: string, timeoutMilis?: number) => void;
}

const SnackbarContext = React.createContext<SnackbarType | null | undefined>(null);

export function useSnackbar() {
    return useContext(SnackbarContext)!;
}

export function SnackbarProvider({ alertProps, children }: { alertProps?: AlertProps; children?: ReactNode | undefined }) {
    const defaultTimeoutMilis = 15000;
    const [messages, setMessages] = useState<SnackbarMessage[]>([]);

    //creating a reference object
    const messagesRef = useRef(messages);
    messagesRef.current = messages;

    const removeMessage = (key: string) => {
        console.log("remove " , key);
        setMessages((prevState) => prevState.filter(m => m.key !== key));
    }

    const addMessage = (message: string, severity: AlertColor, timeoutMilis?: number) => {
        const newMessage = {
            key: crypto.randomUUID(),
            message,
            severity,
            timeout: timeoutMilis ?? defaultTimeoutMilis
        };
        setMessages((prevState) => [...prevState, newMessage]);

        setTimeout(() => {removeMessage(newMessage.key)}, newMessage.timeout);
    };

    const openSnackbar = (message: string, severity: AlertColor, timeoutMilis?: number) => {
        addMessage(message, severity, timeoutMilis);
    };

    const info = (message: string, timeoutMilis?: number) => {
        addMessage(message, 'info', timeoutMilis);
    };

    const success = (message: string, timeoutMilis?: number) => {
        addMessage(message, 'success', timeoutMilis);
    };

    const warning = (message: string, timeoutMilis?: number) => {
        addMessage(message, 'warning', timeoutMilis);
    };

    const error = (message: string, timeoutMilis?: number) => {
        addMessage(message, 'error', timeoutMilis);
    };

    const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }

        if(event?.target) {
            console.log(event, reason);
            const snackbarNodes = messages.map(m => document.getElementById(m.key));   
            const theSnackbar = snackbarNodes.find(n => n?.contains(event.target as HTMLElement))
            if(theSnackbar) {
                removeMessage(theSnackbar.id);
            }
        }
    };

    const snackbarFunctions: SnackbarType = {
        error,
        info,
        open: openSnackbar,
        success,
        warning
    };

    return (
        <SnackbarContext.Provider value={snackbarFunctions}>
            <>
                {children}
                {messages.map((message, index) => (
                    <Snackbar
                        key={message.key}
                        id={message.key}
                        open={true}
                        onClick={handleClose}
                        onClose={handleClose}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        sx={{transform: `translateY(-${58 * index}px)`}}
                    >
                        <Alert elevation={6} variant="filled" onClose={handleClose} severity={message.severity} {...alertProps}>
                            {message.message}
                        </Alert>
                    </Snackbar>
                ))}
            </>
        </SnackbarContext.Provider>
    );
}
